import { logSwallowed } from '@/lib/errors';
import { useState, useEffect } from 'react';
import { getSDK } from '@/store/sdkStore';
import { onDataChanged } from '@/lib/dataEvents';
import { applyFilters } from '@/components/autoforms/AutoListForm';
import { eventBusHub } from '@/lib/eventBusHub';
import { useColumnWidths } from '@/lib/useColumnWidths';
import { createLogger } from '@/lib/logger';

const log = createLogger('MiniDynamicList');

/** Дефолтные ширины (px) колонок дин. списка — по имени колонки (поле таблицы).
 *  Колонки БЕЗ дефолта сохраняют естественную (авто) ширину, пока пользователь
 *  не потянет за границу заголовка — тогда ширина сохраняется per-пользователь. */
const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  name: 200,
  description: 240,
  status: 110,
  assignee: 130,
  code: 140,
};

/** Дефолтные ширины для фактического состава колонок списка */
function defaultWidthsFor(cols: MiniDynamicListColumn[]): Record<string, number> {
  const widths: Record<string, number> = {};
  for (const c of cols) {
    const w = DEFAULT_COLUMN_WIDTHS[c.field];
    if (w) widths[c.field] = w;
  }
  return widths;
}

/** Представление ячейки: серверная {field}_presentation; UUID без представления → «—» */
function cellDisplay(row: Record<string, unknown>, field: string): string {
  const raw = row[field];
  const pres = row[field + '_presentation'];
  if (pres) return String(pres);
  const s = String(raw ?? '');
  if (s.length === 36 && s[8] === '-') return '—';
  return s;
}

interface MiniDynamicListColumn {
  field: string;
  label?: string;
}

interface DLFilter {
  field: string;
  condition: string;
  value?: string;
}

interface MiniDynamicListProps {
  table: string;
  columns?: MiniDynamicListColumn[];
  /** Отборы (@objectId уже резолвнут передатчиком) */
  filters?: DLFilter[];
  /** Отбор дин. списка ЗАВЕДОМО пуст (незаполненная привязка @objectId/@param/…
   *  — резолв lib/dlFilters): таблицу НЕ грузим («без отбора» показывал бы все
   *  записи базы) — список пустой до заполнения связи. */
  forceEmpty?: boolean;
  height?: number;
  disabled?: boolean;
  /** Вид (как в 1С): 'cards' — карточки, иначе таблица */
  viewMode?: 'list' | 'tree' | 'flat' | 'cards';
  /** «Создать» — открывает форму объекта источника с предзаполнением из отборов */
  onCreate?: () => void;
  /** Клик по строке/карточке — открыть запись источника (для дин.списка в форме) */
  onRowClick?: (id: string) => void;
}

/**
 * Динамический список в форме (компактная таблица).
 * Данные — НАПРЯМУЮ с сервера (listTableData: SSoT, представления ссылок
 * `{col}_presentation`, отборы «=» фильтрует сервер); условия кроме «=» —
 * клиентская доработка поверх. Обновление — СОБЫТИЙНОЕ (шина: task.*, order.*,
 * data.changed.*) — никаких таймеров.
 */
export function MiniDynamicList({ table, columns, filters, forceEmpty, height, disabled, viewMode, onCreate, onRowClick }: MiniDynamicListProps) {
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Локальное событие «данные изменились» (после «+ Создать»/сохранения) —
  // перечитываем список СРАЗУ, не дожидаясь серверной шины и без перезахода.
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => onDataChanged((d) => {
    if (d.table === table) setRefreshTick((t) => t + 1);
  }), [table]);

  useEffect(() => {
    if (!table) return;
    let cancelled = false;

    const load = async () => {
      if (forceEmpty) {
        // Отбор гарантированно пуст (незаполненная привязка @objectId и т.п.):
        // НЕ выполняем выборку «без отбора» — список пустой по построению.
        setRows([]);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Серверные отборы «=» (SSoT): @objectId уже резолвнут передающим кодом
        const serverFilters = (filters || [])
          .filter((f) => f.field && f.condition === '=' && f.value !== undefined && f.value !== '')
          .map((f) => ({ column: f.field, value: String(f.value) }));
        const resp: any = await getSDK().metadata.listTableData({
          schema: 'public',
          table,
          pageSize: 200,
          // page — НУЛЕВОЙ offset (сервер: offset = page × pageSize). page:1 даёт
          // offset 200 → пустой список (динамические списки ничего не показывали).
          page: 0,
          ...(serverFilters.length > 0 ? { filters: serverFilters } : {}),
        } as any);
        if (cancelled) return;
        let parsed: Array<Record<string, unknown>> = ((resp?.rows as any[]) || []).map((r) => {
          const obj: Record<string, unknown> = { id: r.id };
          for (const v of r.values || []) obj[v.column] = v.value;
          return obj;
        });
        // Условия кроме «=» (like, !=, >…) — клиентская доработка поверх серверных
        const clientOnly = (filters || []).filter((f) => f.condition !== '=');
        if (clientOnly.length > 0) parsed = applyFilters(parsed, clientOnly as any);
        setRows(parsed);
        // Диагностика дин.списка (в журнал): что ушло и сколько вернулось
        void import('@/lib/eventLog').then((m) => m.eventLog.info('ui',
          `DL: table=${table} rows=${parsed.length}`, { details: JSON.stringify({ filters: serverFilters }) })).catch((e) => { logSwallowed(e, 'components/forms/MiniDynamicList.tsx:99'); return undefined; });
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Ошибка загрузки');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    // Событийная перезагрузка (без поллинга): создание/изменение задач, заявок,
    // общие изменения данных — по шине событий сервера
    const unsub = eventBusHub.subscribe((ev) => {
      const t = ev.topic || '';
      if (t.startsWith('task.') || t.startsWith('order.') || t.startsWith('data.')) {
        void load();
      }
    });

    return () => {
      cancelled = true;
      unsub();
    };
    // deps по СОДЕРЖИМОМУ фильтров (JSON), НЕ по identity: передатчик (кастомная
    // форма) создаёт новый массив filters на каждый рендер → ввод в любое поле
    // формы перезагружал список (задачи/заявки подчинённые мигали). Стабильный
    // ключ = перезагрузка только при РЕАЛЬНОМ изменении отбора.
  }, [table, JSON.stringify(filters || []), forceEmpty, refreshTick]);

  // Колонки: заданные в макете или все несистемные поля первой строки.
  // Вычисляются ДО ранних return — фактический состав нужен таблице с ширинами
  // колонок (useColumnWidths инициализируется при монтировании).
  const cols = columns && columns.length > 0
    ? columns
    : rows.length > 0
      ? Object.keys(rows[0])
          .filter((k) => !['id', 'created_at', 'updated_at', 'created_by', 'updated_by', 'is_marked'].includes(k) && !k.endsWith('_presentation'))
          .map((k) => ({ field: k, label: k }))
      : [];

  if (loading) {
    return <div className="text-xs text-muted-foreground p-2">Загрузка списка...</div>;
  }
  if (error) {
    return <div className="text-xs text-destructive p-2">{error}</div>;
  }

  return (
    <div className={`border rounded-md flex flex-col overflow-hidden ${disabled ? 'opacity-60 pointer-events-none' : ''}`} style={{ height: height || 160 }}>
      {onCreate && (
        // Кнопка «+ Создать» — ВНЕ скролла (шапка списка), не уезжает и не перекрывается
        <div className="shrink-0 flex items-center px-2 py-1 border-b bg-muted/30">
          <button type="button" onClick={onCreate} className="text-[11px] text-primary hover:underline">
            + Создать
          </button>
        </div>
      )}
      {viewMode === 'cards' ? (
        <div className="flex-1 min-h-0 overflow-auto grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2 p-2">
          {rows.length === 0 && <div className="text-xs text-muted-foreground p-2">Нет данных</div>}
          {rows.map((row, i) => (
            <div
              key={String(row.id ?? i)}
              className={`border rounded p-2 hover:bg-muted/30 ${onRowClick ? 'cursor-pointer' : ''}`}
              onClick={onRowClick ? () => onRowClick(String(row.id ?? '')) : undefined}
            >
              <div className="text-xs font-medium truncate mb-1">{String(row.name ?? row.code ?? row.id ?? '')}</div>
              {cols.slice(0, 3).map((c) => (
                <div key={c.field} className="text-[10px] text-muted-foreground flex justify-between gap-1">
                  <span className="truncate">{c.label || c.field}</span>
                  <span className="truncate">{cellDisplay(row, c.field)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        // Таблица с регулируемыми колонками — отдельный компонент: сохранённые
        // ширины читаются из настроек пользователя ОДИН раз, когда состав
        // колонок уже известен (cols вычислен выше)
        <MiniDynamicTable table={table} cols={cols} rows={rows} onRowClick={onRowClick} />
      )}
    </div>
  );
}

interface MiniDynamicTableProps {
  table: string;
  cols: MiniDynamicListColumn[];
  rows: Array<Record<string, unknown>>;
  onRowClick?: (id: string) => void;
}

/**
 * Таблица дин. списка: ширина колонок — ЕДИНЫЙ механизм useColumnWidths
 * (ключ `miniDl:{таблица}`; перетаскивание правой границы заголовка, мышь+тач;
 * сохраняется per-пользователь — SSoT сервер). Тело списка скроллится ОТДЕЛЬНО;
 * заголовок колонок ЗАКРЕПЛЁН (sticky).
 */
function MiniDynamicTable({ table, cols, rows, onRowClick }: MiniDynamicTableProps) {
  const { widths, startResize } = useColumnWidths(`miniDl:${table}`, defaultWidthsFor(cols));

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      <table className="w-full text-xs border-collapse">
        <thead className="sticky top-0 z-10">
          <tr className="border-b bg-muted/70 backdrop-blur">
            {cols.map((c) => {
              const w = widths[c.field];
              return (
                <th
                  key={c.field}
                  className="relative px-2 py-1 text-left font-medium whitespace-nowrap"
                  style={w ? { width: w } : undefined}
                >
                  {c.label || c.field}
                  {/* Ручка ширины: перетаскивание правой границы заголовка */}
                  <span
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none touch-none"
                    onPointerDown={(e) => startResize(c.field, e)}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length || 1} className="px-2 py-4 text-center text-muted-foreground">Нет данных</td></tr>
          )}
          {rows.map((row, i) => (
            <tr
              key={String(row.id ?? i)}
              className={`border-b last:border-b-0 hover:bg-muted/30 ${onRowClick ? 'cursor-pointer' : ''}`}
              onClick={onRowClick ? () => onRowClick(String(row.id ?? '')) : undefined}
            >
              {cols.map((c) => {
                const w = widths[c.field];
                return (
                  <td
                    key={c.field}
                    // max-w ограничивает только колонки БЕЗ заданной ширины;
                    // у заданных — ровно width (иначе ширина не применилась бы)
                    className={`px-2 py-1 whitespace-nowrap overflow-hidden text-ellipsis${w ? '' : ' max-w-[180px]'}`}
                    style={w ? { width: w } : undefined}
                    title={String(row[c.field] ?? '')}
                  >
                    {cellDisplay(row, c.field)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}